































































































































































































































import Component, { mixins } from 'vue-class-component';
import { currencyFormat, loadWidget } from '@/utils/helpers';
import { namespace } from 'vuex-class';
import { IAnalyticsDailySuccessful } from '@/types/types';
import { Watch } from 'vue-property-decorator';
import WidgetMixins from '@/mixins/WidgetMixins';
import moment from 'moment';
import qs from 'qs';

const analyticsModule = namespace('analytics');

@Component({
  name: 'AnalyticsDailySuccessful',
  components: {
    InternetConnection: loadWidget('pages/InternetConnection'),
    ToolBar: loadWidget('widgets/ToolBar'),
    SnackBar: loadWidget('widgets/SnackBar'),
  },
  filters: {
    currencyFormat,
    numberFormat: (value: number) => (value ?? 0).toLocaleString(),
  },
})
export default class AnalyticsDailySuccessful extends mixins(WidgetMixins) {
  @analyticsModule.Getter('getDailySuccessful') dailySuccessful!: IAnalyticsDailySuccessful | null;

  startDateMenu: boolean = false;
  endDateMenu: boolean = false;
  startDate: string = moment()
    .subtract(30, 'days')
    .format('YYYY-MM-DD');
  endDate: string = moment().format('YYYY-MM-DD');

  headers = [
    { text: 'Date', value: 'date' },
    { text: 'CU Successful', value: 'cuCount', align: 'end' },
    { text: 'CU Amount', value: 'cuAmount', align: 'end' },
    { text: 'SMS Records Sent', value: 'smsCount', align: 'end' },
    { text: 'SMS Segments', value: 'smsSegments', align: 'end' },
    { text: 'SMS Purchases', value: 'purchaseCount', align: 'end' },
    { text: 'Purchase Amount', value: 'purchaseAmount', align: 'end' },
  ];

  ipChartOptions: any = {
    chart: {
      type: 'bar',
      height: 300,
      background: 'transparent',
      fontFamily: 'Hanken Grotesk, sans-serif',
      toolbar: { show: false },
    },
    plotOptions: { bar: { horizontal: false, columnWidth: '45%', borderRadius: 4 } },
    dataLabels: { enabled: false },
    xaxis: { categories: [] },
    legend: { position: 'top' },
  };

  chartOptions: any = {
    chart: {
      type: 'area',
      height: 350,
      background: 'transparent',
      fontFamily: 'Hanken Grotesk, sans-serif',
      toolbar: { show: false },
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    xaxis: { categories: [] },
    yaxis: [],
    legend: { position: 'top' },
  };

  get isDark(): boolean {
    return this.$vuetify.theme.dark;
  }

  get rows(): Array<{
    date: string;
    cuCount: number;
    cuAmount: number;
    smsCount: number;
    smsSegments: number;
    purchaseCount: number;
    purchaseAmount: number;
  }> {
    if (!this.dailySuccessful) return [];
    const smsByDate = new Map(this.dailySuccessful.sms.map(row => [row.date, row]));
    const cuByDate = new Map(this.dailySuccessful.cu.map(row => [row.date, row]));
    const purchasesByDate = new Map((this.dailySuccessful.smsPurchases || []).map(row => [row.date, row]));
    const dates = [...new Set([...smsByDate.keys(), ...cuByDate.keys(), ...purchasesByDate.keys()])].sort();
    return dates.map(date => ({
      date,
      cuCount: cuByDate.get(date)?.count ?? 0,
      cuAmount: cuByDate.get(date)?.amount ?? 0,
      smsCount: smsByDate.get(date)?.count ?? 0,
      smsSegments: smsByDate.get(date)?.segments ?? 0,
      purchaseCount: purchasesByDate.get(date)?.count ?? 0,
      purchaseAmount: purchasesByDate.get(date)?.amount ?? 0,
    }));
  }

  // Chart keeps chronological (ascending) order; the table shows newest first.
  get tableRows(): Array<{ date: string }> {
    return [...this.rows].reverse();
  }

  get smsSegments(): number {
    return this.dailySuccessful?.totals?.sms?.segments ?? 0;
  }

  get smsByIp(): Array<{ ipAddress: string; count: number; segments: number }> {
    return this.dailySuccessful?.smsByIp ?? [];
  }

  get ipSeries(): any[] {
    return [
      { name: 'Records Sent', data: this.smsByIp.map(row => row.count) },
      { name: 'Segments', data: this.smsByIp.map(row => row.segments) },
    ];
  }

  get series(): any[] {
    return [
      { name: 'CU Successful', data: this.rows.map(row => row.cuCount) },
      { name: 'SMS Records Sent', data: this.rows.map(row => row.smsCount) },
    ];
  }

  @Watch('isDark', { immediate: true })
  onThemeChange(dark: boolean): void {
    const foreColor = dark ? '#9AA3AE' : '#545D68';
    const borderColor = dark ? 'rgba(255, 255, 255, 0.1)' : '#E4E7EB';
    this.chartOptions = {
      ...this.chartOptions,
      theme: { mode: dark ? 'dark' : 'light' },
      tooltip: { theme: dark ? 'dark' : 'light' },
      grid: { borderColor },
      chart: { ...this.chartOptions.chart, foreColor, background: 'transparent' },
      colors: [dark ? '#34D399' : '#10B981', dark ? '#5B8BF5' : '#2F6DF0'],
    };
    this.ipChartOptions = {
      ...this.ipChartOptions,
      theme: { mode: dark ? 'dark' : 'light' },
      tooltip: { theme: dark ? 'dark' : 'light' },
      grid: { borderColor },
      chart: { ...this.ipChartOptions.chart, foreColor, background: 'transparent' },
      colors: [dark ? '#5B8BF5' : '#2F6DF0', dark ? '#E0A52B' : '#CF8A09'],
      xaxis: { categories: this.smsByIp.map(row => row.ipAddress) },
    };
  }

  @Watch('dailySuccessful')
  onDataChange(): void {
    // categories come from the response, so refresh them when it lands
    this.ipChartOptions = {
      ...this.ipChartOptions,
      xaxis: { categories: this.smsByIp.map(row => row.ipAddress) },
    };
  }

  @Watch('rows')
  onRowsChange(rows: Array<{ date: string }>): void {
    this.chartOptions = {
      ...this.chartOptions,
      xaxis: { categories: rows.map(row => row.date) },
      yaxis: [
        { title: { text: 'CU Transactions' }, seriesName: 'CU Successful' },
        { opposite: true, title: { text: 'SMS Records Sent' }, seriesName: 'SMS Records Sent' },
      ],
    };
  }

  @Watch('startDate')
  onStartDateChange(): void {
    this.fetch();
  }

  @Watch('endDate')
  onEndDateChange(): void {
    this.fetch();
  }

  fetch(): void {
    const query = qs.stringify({ startDate: this.startDate, endDate: this.endDate }, { addQueryPrefix: true });
    this.$store.dispatch('analytics/fetchDailySuccessful', query);
  }

  created(): void {
    this.fetch();
  }
}
